import React, { useState, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, User, Loader2, AlertCircle, CheckCircle, ChevronRight, BookOpen } from 'lucide-react';
// 1. IMPORT TRANSLATION HOOK
import { useTranslation } from 'react-i18next';

const Login = () => {
  // 2. INITIALIZE HOOK
  const { t } = useTranslation(); 

  const [identifier, setIdentifier] = useState(''); 
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const location = useLocation();
  const queryReason = useMemo(() => {
    return new URLSearchParams(location.search).get('reason');
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(identifier, password);

      if (!user.isActive) {
        setError('This account has been deactivated.');
        return;
      }

      if (user.role === 'ADMIN') {
        navigate('/admin/dashboard');
      } else if (user.role === 'TUTOR') {
        navigate('/tutor/home');
      } else {
        navigate('/student/home');
      }
    } catch (err: any) {
      setError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper px-4 py-8 min-h-screen overflow-y-auto pb-24 flex items-center justify-center">
      <div className="auth-card">

        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl shadow-lg shadow-blue-600/20 mb-6">
            <BookOpen className="text-white" size={32} />
          </div>
          {/* 3. TRANSLATED TEXT */}
          <h2 className="text-3xl font-black text-white tracking-tighter uppercase italic">
            {t('app_name')}
          </h2>
          <p className="mt-2 text-slate-400 font-bold text-[10px] uppercase tracking-[0.2em] italic">
            {t('welcome_back')}
          </p>
        </div>

        {queryReason === 'password_changed' && !error && (
          <div className="mb-6 bg-emerald-500/10 border-l-4 border-emerald-500 p-4 rounded-xl text-emerald-400 text-xs font-bold flex items-center gap-2">
            <CheckCircle size={18} className="shrink-0" />
            <span>Security Updated: Password changed successfully. Please log back in.</span>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-500/10 border-l-4 border-red-500 p-4 rounded-xl text-red-400 text-xs font-bold animate-shake">
            <AlertCircle size={18} className="inline mr-2" /> {error}
          </div>
        )}

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-5">

            <div className="relative">
              <label className="text-[10px] font-black uppercase text-slate-500 ml-4 mb-2 block tracking-widest">
                {t('email_or_phone')}
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <input
                  type="text" 
                  required
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="auth-input pl-12"
                  placeholder="09... or name@example.com"
                />
              </div>
            </div>

            <div className="relative">
              <div className="flex justify-between items-center ml-4 mb-2">
                <label className="text-[10px] font-black uppercase text-slate-500 block tracking-widest">
                  {t('password')}
                </label>
                <Link to="/forgot-password" className="text-blue-500 font-black text-[10px] hover:underline">
                  {t('forgot_password')}
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="auth-input pl-12"
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl transition-all active:scale-95 disabled:bg-slate-800 disabled:text-slate-600 flex items-center justify-center gap-3 border-none cursor-pointer"
          >
            {loading ? (
              <Loader2 className="animate-spin h-5 w-5" />
            ) : (
              <>{t('sign_in')} <ChevronRight size={18} /></>
            )}
          </button>

          <div className="text-center mt-8 pt-6 border-t border-white/5">
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
              {t('new_to_atrons')}{' '}
              <Link
                to="/signup"
                className="text-blue-400 font-black underline underline-offset-4 ml-1"
              >
                {t('create_account')}
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;